<template>
  <div class="flex items-center justify-center min-h-[60vh] py-8 px-4">
    <div class="text-center max-w-sm w-full p-6">
      <div v-if="loading" class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      <div v-else-if="error" class="text-red-600">
        <svg class="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          ></path>
        </svg>
        <p class="text-lg font-semibold">Authentication Failed</p>
        <p class="mt-2 text-gray-600 text-sm">{{ error }}</p>
        <RouterLink to="login" class="mt-4 inline-block text-indigo-600 hover:text-indigo-500 font-medium"> Try again </RouterLink>
      </div>
      <div v-else>
        <svg class="w-12 h-12 mx-auto mb-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <p class="text-lg font-semibold text-gray-900">Authentication Successful!</p>
        <p class="mt-2 text-gray-600 text-sm">Redirecting you to the application...</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useLsGeneralStore } from '../stores/application'
import { successNotification, errorNotification } from '@/utils/notifications'

const store = useLsGeneralStore()
const router = useRouter()
const loading = ref(true)
const error = ref('')

onMounted(async () => {
  try {
    // Get the temporary token from URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const tempToken = urlParams.get('token')

    if (!tempToken) {
      throw new Error('No temporary token found in URL')
    }

    // Exchange temporary token for JWT and user data
    const response = await fetch('/api/v1/oauth/exchange_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: tempToken }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to exchange temporary token')
    }

    const data = await response.json()

    if (!data.jwt) {
      throw new Error('No JWT token received')
    }

    // Set the authentication data in the store
    store.setJwt(data.jwt)
    store.account = data.account
    store.workspace = data.workspace

    successNotification('Successfully signed in with Google!')

    // Redirect to home page after a short delay
    setTimeout(() => {
      router.push({ name: 'home' })
    }, 1500)
  } catch (err) {
    console.error('OAuth success page error:', err)
    error.value = err.message || 'An unexpected error occurred'
    errorNotification('Authentication failed')
  } finally {
    loading.value = false
  }
})
</script>
